import { createAnthropic } from '@ai-sdk/anthropic'
import { createGoogleGenerativeAI } from '@ai-sdk/google'
import { createGroq } from '@ai-sdk/groq'
import { createOpenAI } from '@ai-sdk/openai'
import { createOpenRouter } from '@openrouter/ai-sdk-provider'
import type { LanguageModel } from 'ai'
import { getUserSettings } from '@/types/UserSettings'

export type ProviderType =
  | 'openai'
  | 'anthropic'
  | 'google'
  | 'groq'
  | 'openrouter'

export interface ModelInfo {
  id: string
  description?: string
  created?: string
}

export interface ProviderConfig {
  name: string
  apiKeyField: string
  fetchModels: (apiKey: string) => Promise<ModelInfo[]>
  createModel: (apiKey: string, modelId: string) => LanguageModel
}

const fetchOpenAIModels = async (apiKey: string): Promise<ModelInfo[]> => {
  const response = await fetch('https://api.openai.com/v1/models', {
    headers: { Authorization: `Bearer ${apiKey}` },
  })

  if (!response.ok) throw new Error('Failed to fetch OpenAI models')

  const data = await response.json()
  return data.data
    .filter((model: { id: string }) => model.id.includes('gpt'))
    .map((model: { id: string; created: number }) => ({
      id: model.id,
      description: `OpenAI ${model.id}`,
      created: new Date(model.created * 1000).toISOString(),
    }))
}

const fetchAnthropicModels = async (apiKey: string): Promise<ModelInfo[]> => {
  const response = await fetch('https://api.anthropic.com/v1/models', {
    headers: {
      'x-api-key': apiKey,
      'anthropic-version': '2023-06-01',
      'anthropic-dangerous-direct-browser-access': 'true',
    },
  })

  if (!response.ok) throw new Error('Failed to fetch Anthropic models')

  const data = await response.json()
  return data.data.map(
    (model: { id: string; display_name?: string; created_at: string }) => ({
      id: model.id,
      description: `Anthropic ${model.display_name || model.id}`,
      created: model.created_at,
    })
  )
}

const fetchGoogleModels = async (apiKey: string): Promise<ModelInfo[]> => {
  const response = await fetch(
    `https://generativelanguage.googleapis.com/v1beta/models?key=${apiKey}`
  )

  if (!response.ok) throw new Error('Failed to fetch Google models')

  const data = await response.json()
  return data.models
    .filter((model: { supportedGenerationMethods?: string[] }) =>
      model.supportedGenerationMethods?.includes('generateContent')
    )
    .map(
      (model: {
        name: string
        description?: string
        displayName?: string
      }) => ({
        id: model.name,
        description:
          model.description || `Google ${model.displayName || model.name}`,
      })
    )
}

const fetchGroqModels = async (apiKey: string): Promise<ModelInfo[]> => {
  const response = await fetch('https://api.groq.com/openai/v1/models', {
    headers: { Authorization: `Bearer ${apiKey}` },
  })

  if (!response.ok) throw new Error('Failed to fetch Groq models')

  const data = await response.json()
  return data.data.map((model: { id: string; created: number }) => ({
    id: model.id,
    description: `Groq ${model.id}`,
    created: new Date(model.created * 1000).toISOString(),
  }))
}

const fetchOpenRouterModels = async (apiKey: string): Promise<ModelInfo[]> => {
  const response = await fetch('https://openrouter.ai/api/v1/models', {
    headers: {
      Authorization: `Bearer ${apiKey}`,
      'Content-Type': 'application/json',
    },
  })

  if (!response.ok) throw new Error('Failed to fetch OpenRouter models')

  const data = await response.json()
  return data.data.map(
    (model: { id: string; description?: string; created?: number }) => ({
      id: model.id,
      description: model.description || `OpenRouter ${model.id}`,
      created: model.created
        ? new Date(model.created * 1000).toISOString()
        : undefined,
    })
  )
}

export const PROVIDERS: Record<ProviderType, ProviderConfig> = {
  openai: {
    name: 'OpenAI',
    apiKeyField: 'openaiApiKey',
    fetchModels: fetchOpenAIModels,
    createModel: (apiKey: string, modelId: string) => {
      return createOpenAI({ apiKey: apiKey })(modelId)
    },
  },
  anthropic: {
    name: 'Anthropic',
    apiKeyField: 'anthropicApiKey',
    fetchModels: fetchAnthropicModels,
    createModel: (apiKey: string, modelId: string) => {
      return createAnthropic({
        apiKey: apiKey,
        headers: { 'anthropic-dangerous-direct-browser-access': 'true' },
      })(modelId)
    },
  },
  google: {
    name: 'Google',
    apiKeyField: 'googleApiKey',
    fetchModels: fetchGoogleModels,
    createModel: (apiKey: string, modelId: string) => {
      return createGoogleGenerativeAI({ apiKey: apiKey })(modelId)
    },
  },
  groq: {
    name: 'Groq',
    apiKeyField: 'groqApiKey',
    fetchModels: fetchGroqModels,
    createModel: (apiKey: string, modelId: string) => {
      return createGroq({ apiKey: apiKey })(modelId)
    },
  },
  openrouter: {
    name: 'OpenRouter',
    apiKeyField: 'openrouterApiKey',
    fetchModels: fetchOpenRouterModels,
    createModel: (apiKey: string, modelId: string) => {
      return createOpenRouter({ apiKey: apiKey })(modelId)
    },
  },
}

export const getAvailableProviders = (): ProviderType[] => {
  const settings = getUserSettings()
  const availableProviders: ProviderType[] = []

  Object.entries(PROVIDERS).forEach(([providerId, config]) => {
    const apiKeyField = config.apiKeyField
    if (settings[apiKeyField as keyof typeof settings]?.content) {
      availableProviders.push(providerId as ProviderType)
    }
  })

  return availableProviders
}

export const fetchProviderModels = async (
  providerType: ProviderType
): Promise<ModelInfo[]> => {
  const settings = getUserSettings()
  const config = PROVIDERS[providerType]
  const apiKey = settings[config.apiKeyField as keyof typeof settings]?.content

  if (!apiKey) {
    throw new Error(`API key not found for ${config.name}`)
  }

  return await config.fetchModels(apiKey)
}

export const getModel = (providerType: ProviderType, modelId: string) => {
  const settings = getUserSettings()
  const config = PROVIDERS[providerType]
  const apiKey = settings[config.apiKeyField as keyof typeof settings]?.content

  if (!apiKey) {
    throw new Error(`API key not found for ${config.name}`)
  }

  return config.createModel(apiKey, modelId)
}
